import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useMedia } from 'react-use'
import Checkbox from '../elements/Checkbox'
import { TimelineLite } from 'gsap'
import DisabledOverlay from './DisabledOverlay'
import LoadingOverlay from './LoadingOverlay'

const ParentalControlTable = (props) => {
    const isMobile = useMedia('(max-width: 992px)')
    const items = []
    const rects = []
    const expandLimit = 4

    const container = useRef(null)
    const expandableList = useRef(null)
    const expandableListRect = useRef(null)
    const [isLoadingAll, setIsLoadingAll] = useState(false)
    const [isListExpanded, setIsListExpanded] = useState(false)
    const [changedCategories, setChangedCategories] = useState([])
    const [hasParentalControlSelected, setHasParentalControlSelected] = useState(false)
    const [hasFocusTimeSelected, setHasFocusTimeSelected] = useState(false)
    const [categoriesWithIds, setCategoriesWithIds] = useState({})
    const [isLastItemExpanded, setIsLastItemExpanded] = useState(0)

    useEffect(() => {
        const categories = {}
        props.categories.forEach(category => {
            categories[category.categoryId] = category
        })
        setCategoriesWithIds(categories)
        updateSelections(categories)
    }, [props.categories])

    const updateSelections = (categories = categoriesWithIds) => {
        setHasParentalControlSelected(false)
        setHasFocusTimeSelected(false)
        Object.keys(categories).forEach(categoryId => {
            const category = categories[categoryId]
            if (category.currentlyBlocked) {
                setHasParentalControlSelected(true)
            }

            if (category.quietTimeBlocked) {
                setHasFocusTimeSelected(true)
            }
        })
    }

    const getCategoryKeyById = (categoryId, categories) => {
        return categories.findIndex(category => category.categoryId === categoryId)
    }

    const handleToggleParentalControl = (item, value) => {
        setChangedCategories([...changedCategories, item.categoryId])
        const categories = [...props.categories]
        const key = getCategoryKeyById(item.categoryId, categories)
        categories[key] = { ...categories[key], currentlyBlocked: value }
        categories[key].quietTimeBlocked = !!value
        handleSetCategories(categories)
    }

    const handleToggleQuietTime = (categoryId, value) => {
        setChangedCategories([...changedCategories, categoryId])
        const categories = [...props.categories]
        const key = getCategoryKeyById(categoryId, categories)
        categories[key] = { ...categories[key], quietTimeBlocked: value }
        handleSetCategories(categories)
    }

    const handleDisableAllParentalControl = () => {
        setIsLoadingAll(true)
        const categories = [...props.categories]
        categories.forEach((item, key) => {
            categories[key].currentlyBlocked = false
        })

        handleSetCategories(categories, true)
    }

    const handleDisableAllQuietTime = () => {
        setIsLoadingAll(true)
        const categories = [...props.categories]
        categories.forEach((item, key) => {
            if (!item.currentlyBlocked) {
                categories[key].quietTimeBlocked = false
            }
        })

        handleSetCategories(categories, true)
    }

    const handleSetCategories = (categories, noTimer = false) => {
        props.handleSetCategories(categories, props.type, noTimer).then(() => {
            setChangedCategories([])
            updateSelections(categories)
            setIsLoadingAll(false)
        }).catch(() => {
            setChangedCategories([])
            setIsLoadingAll(false)
        })
    }

    const handleClick = (categoryId, count) => {
        const item = items[categoryId]
        const rect = rects[categoryId]

        if (items[categoryId].classList.contains('closed')) {
            animateOpen(item, rect, () => {
                // If they clicked the last item but the list is not expanded we
                // need to update the height so the items expanded copy can be seen
                if (count === expandLimit && !isListExpanded) {
                    const element = items[categoryId].querySelector('.expandable')
                    if (element) {
                        const clientRect = element.getBoundingClientRect()
                        setIsLastItemExpanded(clientRect.height)
                    }
                }
            })
        } else {
            animateClosed(item, rect, () => {
                if (count === expandLimit) {
                    setIsLastItemExpanded(0)
                }
            })
        }
    }

    const animateOpen = (item, rect, onComplete = () => {}) => {
        item.classList.remove('closed')
        item.classList.add('open')

        const tl = new TimelineLite()
        const content = item.querySelector('.expandable')

        tl.set(content, { display: 'block', height: 'auto' })
        tl.fromTo(content, 0.25, { height: 0 }, { height: 'auto', opacity: 1, paddingTop: '15px', paddingBottom: '15px' })
        if (!isListExpanded) {
            tl.to(expandableList.current, 0.25, { height: `${(expandLimit * 55) + (isMobile ? 170 : 60)}px` }, 0)
        }
        tl.add(onComplete)
        tl.to(rect, 0.2, { rotation: 90, transformOrigin: '50% 50%' }, 0)
    }

    const animateClosed = (item, rect, onComplete = () => {}) => {
        item.classList.remove('open')
        item.classList.add('closed')

        const tl = new TimelineLite()
        const content = item.querySelector('.expandable')

        tl.to(content, 0.25, { height: 0, paddingTop: 0, paddingBottom: 0, opacity: 0 })
        if (!isListExpanded) {
            tl.to(expandableList.current, 0.25, { height: `${(expandLimit * 55) + (isMobile ? 170 : 60)}px` }, 0)
        }
        tl.add(onComplete)
        tl.to(rect, 0.2, { rotation: 0, transformOrigin: '50% 50%' }, 0)
    }

    const handleToggleExpandList = event => {
        event.preventDefault()
        const tl = new TimelineLite()

        if (!isListExpanded) {
            setIsListExpanded(true)
            setIsLastItemExpanded(0)
            tl.set(expandableList.current, { display: 'block', height: 'auto' })
            tl.from(expandableList.current, 0.25, { height: 0 })
            tl.to(expandableListRect.current, 0.2, { rotation: 90, transformOrigin: '50% 50%' }, 0)
            tl.to(window, 0.5, { scrollTo: { y: container.current, offsetY: 80 } })
        } else {
            setIsListExpanded(false)
            tl.to(expandableList.current, 0.25, { height: `${(expandLimit * 55) + (isMobile ? 170 : 60)}px` })
            tl.to(expandableListRect.current, 0.2, { rotation: 0, transformOrigin: '50% 50%' }, 0)
        }
    }

    const Heading = (hideTitle = false) => {
        return (
            <div className={`row header no-gutters ${hideTitle ? 'no-borders' : ''}`}>
                <div className='col-12 col-lg-8 d-flex align-items-center'>
                    <div className='text-center text-lg-left'>{hideTitle ? '' : props.title}</div>
                </div>
                <div className='col-2 text-center d-none d-lg-block'>
                    <p className='font-xsmall m-0 regular'>Blocks if</p>
                    <p className='m-0 lh'>Parental control</p>
                    <p className='font-xsmall m-0 mb-1 regular'>is enabled</p>
                    <div>{props.categories.filter(val => val.currentlyBlocked === true).length}/{props.categories.length}</div>
                </div>
                <div className='col-2 text-center d-none d-lg-block'>
                    <p className='font-xsmall m-0 regular'>Blocks during</p>
                    <p className='m-0 lh'>Focus-time</p>
                    <p className='font-xsmall m-0 mb-1 regular'>schedule</p>
                    <div>{props.categories.filter(val => val.quietTimeBlocked === true).length}/{props.categories.length}</div>
                </div>
            </div>
        )
    }

    const DisableAll = () => {
        return (
            <div className='item closed'>
                <LoadingOverlay isLoading={props.isLoading || isLoadingAll}>
                    <DisabledOverlay isDisabled={(!hasParentalControlSelected && !hasFocusTimeSelected)}>
                        <div className='row title no-gutters'>
                            <div className='col-12 col-lg-8 d-flex align-items-center'>
                                Uncheck to clear all selected Parental control or Focus-time only categories
                            </div>
                            {isMobile
                                ? ''
                                : <React.Fragment>
                                    <div className='col-6 col-lg-2 d-flex justify-content-center'>
                                        <DisabledOverlay isDisabled={!hasParentalControlSelected}>
                                            <Checkbox
                                                value={hasParentalControlSelected}
                                                handleClick={handleDisableAllParentalControl}
                                                name='currentlyBlockedAll'
                                                hasCross
                                            />
                                        </DisabledOverlay>
                                    </div>
                                    <div className='col-6 col-lg-2 d-flex justify-content-center'>
                                        <DisabledOverlay isDisabled={!hasFocusTimeSelected}>
                                            <Checkbox
                                                value={hasFocusTimeSelected}
                                                handleClick={handleDisableAllQuietTime}
                                                name='quietTimeBlockedAll'
                                                hasCross
                                            />
                                        </DisabledOverlay>
                                    </div>
                                </React.Fragment>}
                        </div>
                        <div>
                            {isMobile
                                ? <div className='row mt-4'>
                                    <div className='col-6 d-flex align-items-center flex-column'>
                                        <p className='font-small m-0 mb-1 text-center'>Parental control</p>
                                        <DisabledOverlay isDisabled={!hasParentalControlSelected}>
                                            <Checkbox
                                                value={hasParentalControlSelected}
                                                handleClick={handleDisableAllParentalControl}
                                                name='currentlyBlockedAll'
                                                hasCross
                                            />
                                        </DisabledOverlay>
                                    </div>
                                    <div className='col-6 d-flex align-items-center flex-column'>
                                        <p className='font-small m-0 mb-1 text-center'>Focus-time</p>
                                        <DisabledOverlay isDisabled={!hasFocusTimeSelected}>
                                            <Checkbox
                                                value={hasFocusTimeSelected}
                                                handleClick={handleDisableAllQuietTime}
                                                name='quietTimeBlockedAll'
                                                hasCross
                                            />
                                        </DisabledOverlay>
                                    </div>
                                </div>
                                : ''}
                        </div>
                    </DisabledOverlay>
                </LoadingOverlay>
            </div>
        )
    }

    const Category = (item, key) => {
        return (
            <React.Fragment key={key}>
                {key === (props.categories.length / 2) && !isMobile ? Heading(true) : ''}
                <div ref={el => items[item.categoryId] = el} className='item closed'>
                    <LoadingOverlay isLoading={props.isLoading && changedCategories.includes(item.categoryId)}>
                        <div className='row title no-gutters'>
                            <div className='col-12 col-lg-8 d-flex align-items-center' onClick={() => handleClick(item.categoryId, (key + 1))}>
                                <svg width='30px' height='30px' viewBox='0 0 30 30' className='icon'>
                                    <rect ref={el => rects[item.categoryId] = el} fill='#004346' x='13' y='7' width='4' height='16' />
                                    <rect fill='#004346' transform='translate(15.000000, 15.000000) rotate(-90.000000) translate(-15.000000, -15.000000) ' x='13' y='7' width='4' height='16' />
                                </svg>
                                <div className='ml-2 lh' dangerouslySetInnerHTML={{ __html: item.categoryName }} />
                            </div>
                            {isMobile
                                ? ''
                                : <React.Fragment>
                                    <div className='col-6 col-lg-2 d-flex justify-content-center'>
                                        <Checkbox
                                            value={item.currentlyBlocked}
                                            handleClick={value => handleToggleParentalControl(item, value)}
                                            name='currentlyBlocked'
                                            hasCross
                                        />
                                    </div>
                                    <div className='col-6 col-lg-2 d-flex justify-content-center'>
                                        <DisabledOverlay isDisabled={item.currentlyBlocked}>
                                            <Checkbox
                                                value={item.quietTimeBlocked}
                                                handleClick={value => handleToggleQuietTime(item.categoryId, value)}
                                                name='quietTimeBlocked'
                                                hasCross
                                            />
                                        </DisabledOverlay>
                                    </div>
                                </React.Fragment>}
                        </div>
                        <div className='expandable'>
                            <p className='m-0'>{item.categoryDescription}</p>
                            {isMobile
                                ? <div className='row mt-4'>
                                    <div className='col-6 d-flex align-items-center flex-column'>
                                        <p className='font-small m-0 mb-1 text-center'>Parental control</p>
                                        <Checkbox
                                            value={item.currentlyBlocked}
                                            handleClick={value => handleToggleParentalControl(item, value)}
                                            name='currentlyBlocked'
                                            hasCross
                                        />
                                    </div>
                                    <div className='col-6 d-flex align-items-center flex-column'>
                                        <p className='font-small m-0 mb-1 text-center'>Focus-time</p>
                                        <DisabledOverlay isDisabled={item.currentlyBlocked}>
                                            <Checkbox
                                                value={item.quietTimeBlocked}
                                                handleClick={value => handleToggleQuietTime(item.categoryId, value)}
                                                name='quietTimeBlocked'
                                                hasCross
                                            />
                                        </DisabledOverlay>
                                    </div>
                                </div>
                                : ''}
                        </div>
                    </LoadingOverlay>
                </div>
            </React.Fragment>
        )
    }

    const calcHeight = () => {
        return (expandLimit * 55) + (isMobile ? 170 : 55) + isLastItemExpanded
    }

    const sortFirst = ['games', 'moviesAndSeries', 'radioAndTvOnline', 'photoAndVideo']

    return (
        <div ref={container} className='parental-control-table'>
            {Heading()}
            <div ref={expandableList} className='row no-gutters' style={props.expandable ? { height: `${calcHeight()}px`, overflow: 'hidden' } : {}}>
                <div className='col-12 p-0'>
                    {props.hasError
                        ? <div className='item closed'>
                            <div className='row-error'>Sorry an error occurred and your changes were not saved.</div>
                          </div>
                        : ''}
                    {DisableAll()}
                    {props.categories.filter(item => sortFirst.includes(item.categoryId)).map((item, key) => {
                        return Category(item, key)
                    })}
                    {props.categories.filter(item => !sortFirst.includes(item.categoryId)).map((item, key) => {
                        return Category(item, key)
                    })}
                </div>
            </div>
            {props.expandable
                ? <div className='row no-gutters'>
                    <div className='col-12 p-0 text-center'>
                        <div className='item d-flex justify-content-center'>
                            <a href='#' className='d-inline-flex align-items-center' onClick={handleToggleExpandList}>
                                <svg width='20px' height='20px' viewBox='0 0 30 30' className='icon'>
                                    <rect ref={expandableListRect} fill='#63d481' x='13' y='7' width='4' height='16' />
                                    <rect fill='#63d481' transform='translate(15.000000, 15.000000) rotate(-90.000000) translate(-15.000000, -15.000000) ' x='13' y='7' width='4' height='16' />
                                </svg>
                                <span className='d-inline-block'>expand to see all {props.categories.length} categories</span>
                            </a>
                        </div>
                    </div>
                  </div>
                : ''}
        </div>
    )
}

ParentalControlTable.propTypes = {
    title: PropTypes.object.isRequired,
    categories: PropTypes.arrayOf(
        PropTypes.shape(
            {
                categoryId: PropTypes.string,
                categoryName: PropTypes.string,
                categoryDescription: PropTypes.string,
                currentlyBlocked: PropTypes.bool,
                quietTimeBlocked: PropTypes.bool
            }
        )
    ).isRequired,
    handleSetCategories: PropTypes.func.isRequired,
    expandable: PropTypes.bool,
    type: PropTypes.string.isRequired,
    hasError: PropTypes.bool,
    isLoading: PropTypes.bool
}

ParentalControlTable.defaultProps = {
    categories: [],
    expandable: false
}

export default ParentalControlTable



// WEBPACK FOOTER //
// ./resources/js/components/ParentalControlTable.js